import type { RawFieldValue } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { customFieldKeys } from './keys.ts'

/**
 * Custom field hooks. Definitions are org config (cached once, invalidated on
 * any definition change); values are per person and always arrive already
 * joined to their definition, so a screen never has to zip the two together.
 */

export function useCustomFields() {
  const api = useApi()
  return useQuery({
    queryKey: customFieldKeys.definitions(),
    queryFn: async () => {
      const { data, error } = await api.api.v1['custom-fields'].get({ query: { limit: 200 } })
      if (error) throw error
      return data.items
    },
  })
}

export type CustomFieldDefinition = NonNullable<ReturnType<typeof useCustomFields>['data']>[number]

export function usePersonFields(personId: string) {
  const api = useApi()
  return useQuery({
    queryKey: customFieldKeys.values(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.people({ id: personId }).fields.get()
      if (error) throw error
      return data.items
    },
  })
}

export type PersonFieldValue = NonNullable<ReturnType<typeof usePersonFields>['data']>[number]

export function useSetPersonFields(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (values: { fieldId: string; value: RawFieldValue }[]) => {
      const { data, error } = await api.api.v1.people({ id: personId }).fields.put({ values })
      if (error) throw error
      return data.items
    },
    onSuccess: (items) => queryClient.setQueryData(customFieldKeys.values(personId), items),
  })
}

/** Definition management (people:admin, org-wide) — the settings screen. */
export function useCustomFieldMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  // A definition change reshapes every profile's values, not just the list.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() })
  }

  const create = useMutation({
    mutationFn: async (body: Parameters<(typeof api.api.v1)['custom-fields']['post']>[0]) => {
      const { data, error } = await api.api.v1['custom-fields'].post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({
      fieldId,
      ...body
    }: {
      fieldId: string
      label?: string
      description?: string | null
      required?: boolean
      sectionId?: string | null
      sortOrder?: number
      options?: { id?: string; label: string }[]
    }) => {
      const { data, error } = await api.api.v1['custom-fields']({ id: fieldId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (fieldId: string) => {
      const { error } = await api.api.v1['custom-fields']({ id: fieldId }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

// ---- sections ---------------------------------------------------------------------

/** The profile sections (Planning Center's tabs) fields are grouped under. */
export function useCustomFieldSections() {
  const api = useApi()
  return useQuery({
    queryKey: customFieldKeys.sections(),
    queryFn: async () => {
      const { data, error } = await api.api.v1['custom-fields'].sections.get()
      if (error) throw error
      return data.items
    },
  })
}

export type CustomFieldSection = NonNullable<
  ReturnType<typeof useCustomFieldSections>['data']
>[number]

export function useCustomFieldSectionMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  // A section change regroups every profile, so everything custom-field goes stale.
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() })

  const create = useMutation({
    mutationFn: async (body: { name: string; sortOrder?: number }) => {
      const { data, error } = await api.api.v1['custom-fields'].sections.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; sortOrder?: number }) => {
      const { data, error } = await api.api.v1['custom-fields'].sections({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1['custom-fields'].sections({ id }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
  return { create, update, remove }
}

// ---- file fields ------------------------------------------------------------------

/** Where a file field's file is served from; the API proxies it, so the session applies. */
export const personFieldFileUrl = (personId: string, fieldId: string) =>
  `/api/v1/people/${personId}/fields/${fieldId}/file`

/**
 * A file field's file: ask for a presigned URL, PUT the bytes straight to
 * storage, then tell the API it landed — the same three steps as a photo.
 */
export function usePersonFieldFile(personId: string, fieldId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const field = api.api.v1.people({ id: personId }).fields({ fieldId })

  const upload = useMutation({
    mutationFn: async (file: { name: string; type: string; body: Blob }) => {
      const { data, error } = await field['upload-url'].post({
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.body.size,
      })
      if (error) throw error
      const uploaded = await fetch(data.url, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file.body,
      })
      if (!uploaded.ok) throw new Error(`Storage rejected the upload (${uploaded.status}).`)
      const confirmed = await field.file.put({ key: data.key, fileName: file.name })
      if (confirmed.error) throw confirmed.error
      return confirmed.data.items
    },
    onSuccess: (items) => queryClient.setQueryData(customFieldKeys.values(personId), items),
  })

  const clear = useMutation({
    mutationFn: async () => {
      const { data, error } = await field.file.delete()
      if (error) throw error
      return data.items
    },
    onSuccess: (items) => queryClient.setQueryData(customFieldKeys.values(personId), items),
  })

  return { upload, clear, url: personFieldFileUrl(personId, fieldId) }
}
